import React, { useState, useEffect } from 'react';
import {
  upsertExternalMapping,
  deleteExternalMapping,
  ExternalMapping,
  Hospital,
  OperatingRoom
} from '../services/api.ts';
import { useExternalMappings, useHospitals, useOperatingRooms } from '../data/queries.ts';
import AdminOnly from './AdminOnly.tsx';
import ReadOnlyBanner from './ReadOnlyBanner.tsx';

const ExternalMappingsManager: React.FC = () => {
  // All three lists come from the shared cache (data/queries.ts). Saving or deleting a mapping marks the
  // cached mappings stale (api.ts), which refetches them — no manual reload.
  const mappingsQuery = useExternalMappings();
  const hospitalsQuery = useHospitals();
  const roomsQuery = useOperatingRooms();
  const mappings: ExternalMapping[] = mappingsQuery.data ?? [];
  const hospitals: Hospital[] = hospitalsQuery.data ?? [];
  const operatingRooms: OperatingRoom[] = roomsQuery.data ?? [];
  const loading = mappingsQuery.isPending || hospitalsQuery.isPending || roomsQuery.isPending;
  const loadFailed = !!(mappingsQuery.error || hospitalsQuery.error || roomsQuery.error);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Form state for adding new mapping
  const [newMapping, setNewMapping] = useState({
    mappingType: 'location' as 'location' | 'room',
    externalKey: '',
    internalId: '',
    displayLabel: ''
  });
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    if (!loadFailed) return;
    console.error('Error loading data:', mappingsQuery.error || hospitalsQuery.error || roomsQuery.error);
    setMessage({ type: 'error', text: 'Failed to load data' });
  }, [loadFailed]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    setAdding(true);
    setMessage(null);

    try {
      await upsertExternalMapping(newMapping);
      setMessage({ type: 'success', text: 'Mapping created successfully' });
      setNewMapping({ mappingType: 'location', externalKey: '', internalId: '', displayLabel: '' });
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to create mapping'
      });
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (mappingType: string, externalKey: string) => {
    if (!window.confirm(`Delete mapping "${externalKey}"?`)) return;

    try {
      await deleteExternalMapping(mappingType, externalKey);
      setMessage({ type: 'success', text: 'Mapping deleted' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to delete mapping' });
    }
  };

  const locationMappings = mappings.filter(m => m.mappingType === 'location');
  const roomMappings = mappings.filter(m => m.mappingType === 'room');

  const getHospitalName = (id: string) => {
    const h = hospitals.find(h => h.hospitalId === id);
    return h ? h.name : id;
  };

  const getRoomName = (id: string) => {
    const r = operatingRooms.find(r => r.operatingRoomId === id);
    return r ? r.name : id;
  };

  if (loading) {
    return <div className="card"><p>Loading mappings...</p></div>;
  }

  return (
    <div>
      <ReadOnlyBanner />
      <div className="card" style={{ marginBottom: '16px' }}>
        <h2>External Mappings</h2>
        <p style={{ fontSize: '0.9em', color: 'var(--text-muted)' }}>
          Map external system identifiers (location names, room numbers) to internal hospital and operating room IDs.
        </p>

        {message && (
          <div className={`alert alert-${message.type === 'error' ? 'error' : 'success'}`} style={{ marginBottom: '12px' }}>
            {message.text}
          </div>
        )}

        {/* Add Mapping Form */}
        <AdminOnly>
        <div style={{ backgroundColor: 'var(--bg-surface-raised)', padding: '12px', borderRadius: '6px', marginBottom: '16px' }}>
          <h3 style={{ marginTop: 0, fontSize: '1em' }}>Add Mapping</h3>
          <form onSubmit={handleAdd}>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div className="form-group" style={{ marginBottom: '8px' }}>
                <label htmlFor="mappingType" style={{ fontSize: '0.85em' }}>Type</label>
                <select
                  id="mappingType"
                  value={newMapping.mappingType}
                  onChange={(e) => setNewMapping({
                    ...newMapping,
                    mappingType: e.target.value as 'location' | 'room',
                    internalId: ''
                  })}
                  className="form-control"
                  style={{ minWidth: '120px' }}
                >
                  <option value="location">Location</option>
                  <option value="room">Room</option>
                </select>
              </div>

              <div className="form-group" style={{ marginBottom: '8px' }}>
                <label htmlFor="externalKey" style={{ fontSize: '0.85em' }}>
                  External Key {newMapping.mappingType === 'room' ? '(e.g. CPRMC#1)' : '(e.g. CPRMC)'}
                </label>
                <input
                  id="externalKey"
                  type="text"
                  value={newMapping.externalKey}
                  onChange={(e) => setNewMapping({ ...newMapping, externalKey: e.target.value })}
                  className="form-control"
                  required
                  style={{ minWidth: '150px' }}
                />
              </div>

              <div className="form-group" style={{ marginBottom: '8px' }}>
                <label htmlFor="internalId" style={{ fontSize: '0.85em' }}>
                  {newMapping.mappingType === 'location' ? 'Hospital' : 'Operating Room'}
                </label>
                <select
                  id="internalId"
                  value={newMapping.internalId}
                  onChange={(e) => setNewMapping({ ...newMapping, internalId: e.target.value })}
                  className="form-control"
                  required
                  style={{ minWidth: '200px' }}
                >
                  <option value="">Select...</option>
                  {newMapping.mappingType === 'location'
                    ? hospitals.map(h => (
                      <option key={h.hospitalId} value={h.hospitalId}>{h.name}</option>
                    ))
                    : operatingRooms.map(r => (
                      <option key={r.operatingRoomId} value={r.operatingRoomId}>{r.name}</option>
                    ))
                  }
                </select>
              </div>

              <div className="form-group" style={{ marginBottom: '8px' }}>
                <label htmlFor="displayLabel" style={{ fontSize: '0.85em' }}>Label (optional)</label>
                <input
                  id="displayLabel"
                  type="text"
                  value={newMapping.displayLabel}
                  onChange={(e) => setNewMapping({ ...newMapping, displayLabel: e.target.value })}
                  className="form-control"
                  style={{ minWidth: '120px' }}
                />
              </div>

              <button
                type="submit"
                className="btn btn-primary"
                disabled={adding}
                style={{ marginBottom: '8px', height: '38px' }}
              >
                {adding ? 'Adding...' : 'Add'}
              </button>
            </div>
          </form>
        </div>
        </AdminOnly>
      </div>

      {/* Location Mappings */}
      <div className="card" style={{ marginBottom: '16px' }}>
        <h3>Location Mappings ({locationMappings.length})</h3>
        {locationMappings.length === 0 ? (
          <p style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>No location mappings configured</p>
        ) : (
          <table style={{ width: '100%', fontSize: '0.9em' }}>
            <thead>
              <tr>
                <th style={{ padding: '8px 4px', textAlign: 'left' }}>External Key</th>
                <th style={{ padding: '8px 4px', textAlign: 'left' }}>Hospital</th>
                <th style={{ padding: '8px 4px', textAlign: 'left' }}>Label</th>
                <th style={{ padding: '8px 4px', width: '60px' }}></th>
              </tr>
            </thead>
            <tbody>
              {locationMappings.map((m) => (
                <tr key={m.externalKey}>
                  <td style={{ padding: '6px 4px', fontWeight: '500' }}>{m.externalKey}</td>
                  <td style={{ padding: '6px 4px' }}>{getHospitalName(m.internalId)}</td>
                  <td style={{ padding: '6px 4px', color: 'var(--text-muted)' }}>{m.displayLabel || '-'}</td>
                  <td style={{ padding: '6px 4px' }}>
                    <AdminOnly>
                    <button
                      onClick={() => handleDelete(m.mappingType, m.externalKey)}
                      style={{
                        padding: '2px 8px',
                        fontSize: '0.8em',
                        backgroundColor: 'var(--danger)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '3px',
                        cursor: 'pointer'
                      }}
                    >
                      Delete
                    </button>
                    </AdminOnly>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Room Mappings */}
      <div className="card">
        <h3>Room Mappings ({roomMappings.length})</h3>
        {roomMappings.length === 0 ? (
          <p style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>No room mappings configured</p>
        ) : (
          <table style={{ width: '100%', fontSize: '0.9em' }}>
            <thead>
              <tr>
                <th style={{ padding: '8px 4px', textAlign: 'left' }}>External Key</th>
                <th style={{ padding: '8px 4px', textAlign: 'left' }}>Operating Room</th>
                <th style={{ padding: '8px 4px', textAlign: 'left' }}>Label</th>
                <th style={{ padding: '8px 4px', width: '60px' }}></th>
              </tr>
            </thead>
            <tbody>
              {roomMappings.map((m) => (
                <tr key={m.externalKey}>
                  <td style={{ padding: '6px 4px', fontWeight: '500' }}>{m.externalKey}</td>
                  <td style={{ padding: '6px 4px' }}>{getRoomName(m.internalId)}</td>
                  <td style={{ padding: '6px 4px', color: 'var(--text-muted)' }}>{m.displayLabel || '-'}</td>
                  <td style={{ padding: '6px 4px' }}>
                    <AdminOnly>
                    <button
                      onClick={() => handleDelete(m.mappingType, m.externalKey)}
                      style={{
                        padding: '2px 8px',
                        fontSize: '0.8em',
                        backgroundColor: 'var(--danger)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '3px',
                        cursor: 'pointer'
                      }}
                    >
                      Delete
                    </button>
                    </AdminOnly>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default ExternalMappingsManager;
